import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, ChevronLeft, Info, HelpCircle } from 'lucide-react';
import { useTranslation } from './contexts/LocaleContext';

interface Step {
  id: string;
  targetId?: string;
  title: string;
  description: string;
}

export default function OnboardingTour({ 
  onComplete, 
  forceStart = false 
}: { 
  onComplete: () => void; 
  forceStart?: boolean; 
}) {
  const t = useTranslation() as any;
  const [currentStep, setCurrentStep] = useState(-1);
  const [showLocationHint, setShowLocationHint] = useState(false);

  // If forceStart is true, we should ensure we're at step -1 or 0
  useEffect(() => {
    if (forceStart && currentStep === -1) {
      // On mobile, we might want to skip the welcome screen even when forced
      if (window.innerWidth < 1024) {
        setCurrentStep(0);
      }
    }
  }, [forceStart]);
  const [rect, setRect] = useState<{ top: number; left: number; width: number; height: number; bottom: number; right: number } | null>(null);
  const [cardPosition, setCardPosition] = useState<{ top: number; left: number } | null>(null);
  const [isStepChanging, setIsStepChanging] = useState(false);
  const lastScrolledStep = useRef<number>(-1);

  const steps: Step[] = useMemo(() => [
    {
      id: 'basics',
      targetId: 'tour-basics',
      title: t.invoice.onboarding.steps.basics.title,
      description: t.invoice.onboarding.steps.basics.description,
    },
    {
      id: 'template',
      targetId: 'tour-template',
      title: t.invoice.onboarding.steps.template.title,
      description: t.invoice.onboarding.steps.template.description,
    },
    {
      id: 'dueDate',
      targetId: 'tour-due-date',
      title: t.invoice.onboarding.steps.dueDate.title,
      description: t.invoice.onboarding.steps.dueDate.description,
    },
    {
      id: 'parties',
      targetId: 'tour-parties',
      title: t.invoice.onboarding.steps.parties.title,
      description: t.invoice.onboarding.steps.parties.description,
    },
    {
      id: 'manageClients',
      targetId: 'tour-manage-clients',
      title: t.invoice.onboarding.steps.manageClients.title,
      description: t.invoice.onboarding.steps.manageClients.description,
    },
    {
      id: 'items',
      targetId: 'tour-items',
      title: t.invoice.onboarding.steps.items.title,
      description: t.invoice.onboarding.steps.items.description,
    },
    {
      id: 'payment',
      targetId: 'tour-payment',
      title: t.invoice.onboarding.steps.payment.title,
      description: t.invoice.onboarding.steps.payment.description,
    },
    {
      id: 'status',
      targetId: 'tour-status-selector',
      title: t.invoice.onboarding.steps.status.title,
      description: t.invoice.onboarding.steps.status.description,
    },
    {
      id: 'save',
      targetId: 'tour-save',
      title: t.invoice.onboarding.steps.save.title,
      description: t.invoice.onboarding.steps.save.description,
    },
    {
      id: 'final',
      title: t.invoice.onboarding.steps.final.title,
      description: t.invoice.onboarding.steps.final.description,
    }
  ], [t.invoice.onboarding.steps]);

  const updateRect = useCallback((isScroll = false) => {
    let targetEl: HTMLElement | null = null;
    
    if (currentStep >= 0 && currentStep < steps.length) {
      const step = steps[currentStep];
      if (step.targetId) {
        targetEl = document.getElementById(step.targetId);
      }
    } else if (showLocationHint) {
      targetEl = document.getElementById('tour-restart-button');
    }

    if (targetEl) {
      let newRect = targetEl.getBoundingClientRect();
      
      // 1. Scroll handling - move BEFORE rect calculations for the UI
      if (currentStep >= 0 && lastScrolledStep.current !== currentStep) {
        const threshold = 100;
        const isOutside = newRect.top < threshold || 
                          newRect.bottom > window.innerHeight - threshold ||
                          newRect.height > window.innerHeight - threshold;

        if (isOutside) {
          lastScrolledStep.current = currentStep;
          
          const scrollTarget = newRect.height > window.innerHeight * 0.8 
            ? 'start' 
            : 'center';
            
          targetEl.scrollIntoView({ 
            behavior: 'auto', // Use auto for instant jump, then let the frame transition smoothly
            block: scrollTarget,
            inline: 'nearest'
          });
          
          // Re-calculate rect immediately after instant scroll
          newRect = targetEl.getBoundingClientRect();
        } else {
          lastScrolledStep.current = currentStep;
        }
      }

      // 2. Update highlight rect based on potentially updated newRect
      setRect(prev => {
        if (prev && 
            Math.abs(prev.top - newRect.top) < 0.1 && 
            Math.abs(prev.left - newRect.left) < 0.1 && 
            Math.abs(prev.width - newRect.width) < 0.1 && 
            Math.abs(prev.height - newRect.height) < 0.1) {
          return prev;
        }
        return {
          top: newRect.top,
          left: newRect.left,
          width: newRect.width,
          height: newRect.height,
          bottom: newRect.bottom,
          right: newRect.right
        };
      });
      
      // 3. Update card position
      const isSmallScreen = window.innerWidth < 640;
      const padding = isSmallScreen ? 12 : 24;
      const cardWidth = isSmallScreen ? window.innerWidth - 32 : 400;
      const cardHeight = isSmallScreen ? 180 : 240;
      
      let top: number | undefined;
      let left: number | undefined;
      
      const isLargeScreen = window.innerWidth > 1024;

      if (isLargeScreen) {
        if (newRect.right + cardWidth + padding < window.innerWidth) {
          left = newRect.right + padding;
          top = newRect.top + (newRect.height / 2) - (cardHeight / 2);
        } else if (newRect.left - cardWidth - padding > 0) {
          left = newRect.left - cardWidth - padding;
          top = newRect.top + (newRect.height / 2) - (cardHeight / 2);
        }
      }

      if (left === undefined) {
        left = newRect.left + (newRect.width / 2) - (cardWidth / 2);
        left = Math.max(padding / 2, Math.min(left, window.innerWidth - cardWidth - (padding / 2)));
        
        if (newRect.bottom + cardHeight + padding < window.innerHeight) {
          top = newRect.bottom + padding;
        } else if (newRect.top - cardHeight - padding > 0) {
          top = newRect.top - cardHeight - padding;
        } else {
          top = Math.max(padding, window.innerHeight - cardHeight - padding);
        }
      } else {
        top = Math.max(padding, Math.min(top!, window.innerHeight - cardHeight - padding));
      }
      
      const finalTop = Math.round(top);
      const finalLeft = Math.round(left);

      setCardPosition(prev => {
        if (prev && 
            Math.abs(prev.top - finalTop) < 1 && 
            Math.abs(prev.left - finalLeft) < 1) {
          return prev;
        }
        return { top: finalTop, left: finalLeft };
      });
    } else {
      setRect(null);
      setCardPosition(null);
    }
  }, [currentStep, steps, showLocationHint]);

  useEffect(() => {
    // Initial update
    updateRect();

    // Use ResizeObserver for more accurate tracking without constant re-renders from scroll events
    let targetEl: HTMLElement | null = null;
    if (currentStep >= 0 && currentStep < steps.length) {
      targetEl = document.getElementById(steps[currentStep].targetId || '');
    } else if (showLocationHint) {
      targetEl = document.getElementById('tour-restart-button');
    }

    const observer = new ResizeObserver(() => {
      window.requestAnimationFrame(() => updateRect(false));
    });

    if (targetEl) {
      observer.observe(targetEl);
      observer.observe(document.body); // Also observe body for layout shifts
    }

    const handleScroll = () => {
      window.requestAnimationFrame(() => updateRect(true));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [updateRect, currentStep, steps, showLocationHint]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setIsStepChanging(true);
      setCurrentStep(currentStep + 1);
      setTimeout(() => setIsStepChanging(false), 400);
    } else {
      onComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setIsStepChanging(true);
      setCurrentStep(currentStep - 1);
      setTimeout(() => setIsStepChanging(false), 400);
    } else if (currentStep === 0) {
      setCurrentStep(-1);
    }
  };

  const handleSkip = () => {
    setShowLocationHint(true);
    setCurrentStep(-2); // Special state for location hint
  };

  const closeTour = () => {
    onComplete();
  };

  if (currentStep === -1) {
    return (
      <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-slate-900/60">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          className="bg-white dark:bg-slate-800 rounded-[2rem] sm:rounded-[2.5rem] shadow-2xl p-6 sm:p-8 max-w-sm w-full border border-slate-200 dark:border-slate-700 overflow-hidden relative"
        >
          <div className="absolute top-0 left-0 w-full h-1.5 sm:h-2 bg-gradient-to-r from-sky-400 to-sky-500" />
          <div className="flex flex-col items-center text-center space-y-4 sm:space-y-6">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-sky-50 dark:bg-sky-900/30 rounded-xl sm:rounded-2xl flex items-center justify-center text-sky-600 dark:text-sky-400">
              <HelpCircle size={window.innerWidth < 640 ? 24 : 32} />
            </div>
            <h3 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white leading-tight">
              {t.invoice.onboarding.startTitle}
            </h3>
            <div className="flex flex-col w-full gap-2 sm:gap-3 pt-1 sm:pt-2">
              <button
                onClick={() => setCurrentStep(0)}
                className="w-full py-3 sm:py-4 bg-sky-500 hover:bg-sky-600 text-white rounded-xl sm:rounded-2xl font-black transition-all shadow-lg shadow-sky-500/25 text-sm sm:text-base"
              >
                {t.invoice.onboarding.startButton}
              </button>
              <button
                onClick={handleSkip}
                className="w-full py-2.5 sm:py-3 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300 rounded-xl sm:rounded-2xl font-bold transition-all text-sm sm:text-base"
              >
                {t.invoice.onboarding.skipButton}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  if (showLocationHint) {
    return (
      <div className="fixed inset-0 z-[1000] pointer-events-none">
        {rect ? (
          <div 
            className="absolute inset-0 bg-slate-900/40 pointer-events-auto"
            style={{
              clipPath: `polygon(
                0% 0%, 
                0% 100%, 
                ${rect.left}px 100%, 
                ${rect.left}px ${rect.top}px, 
                ${rect.right}px ${rect.top}px, 
                ${rect.right}px ${rect.bottom}px, 
                ${rect.left}px ${rect.bottom}px, 
                ${rect.left}px 100%, 
                100% 100%, 
                100% 0%
              )`
            }}
            onClick={closeTour}
          />
        ) : (
          <div className="absolute inset-0 bg-slate-900/60 pointer-events-auto" onClick={closeTour} />
        )}
        
        {rect && (
          <motion.div
            initial={false}
            animate={{
              top: rect.top - 4,
              left: rect.left - 4,
              width: rect.width + 8,
              height: rect.height + 8,
            }}
            className="absolute border-4 border-sky-400 rounded-2xl z-[1001] pointer-events-none"
          />
        )}

        <div className={`absolute inset-0 flex items-center justify-center p-4 ${cardPosition ? 'pointer-events-none' : ''}`}>
          <motion.div 
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            style={cardPosition ? { 
              position: 'fixed', 
              top: cardPosition.top, 
              left: cardPosition.left,
              margin: 0,
              width: window.innerWidth < 640 ? 'calc(100vw - 32px)' : '400px',
              maxWidth: '100vw'
            } : {
              position: 'relative',
              width: window.innerWidth < 640 ? 'calc(100vw - 32px)' : '400px',
              maxWidth: '100vw'
            }}
            className="bg-white dark:bg-slate-800 rounded-2xl sm:rounded-3xl shadow-2xl p-6 sm:p-8 border border-slate-200 dark:border-slate-700 pointer-events-auto z-[1002]"
          >
            <div className="flex flex-col items-center text-center space-y-4 sm:space-y-6">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-sky-50 dark:bg-sky-900/30 rounded-xl sm:rounded-2xl flex items-center justify-center text-sky-600 dark:text-sky-400">
                <HelpCircle size={window.innerWidth < 640 ? 24 : 32} />
              </div>
              <p className="text-sm sm:text-base text-slate-600 dark:text-slate-300 font-bold leading-relaxed">
                {t.invoice.onboarding.trainingLocation}
              </p>
              <button
                onClick={closeTour}
                className="w-full py-3 sm:py-4 bg-sky-500 hover:bg-sky-600 text-white rounded-xl sm:rounded-2xl font-black transition-all shadow-lg shadow-sky-500/25 text-sm sm:text-base"
              >
                {t.common.ok}
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  const step = steps[currentStep];
  const isLast = currentStep === steps.length - 1;

  return (
    <div className="fixed inset-0 z-[1000] pointer-events-none">
      {/* Semi-transparent overlay with a "hole" for the rect */}
      {rect ? (
          <div 
            className="absolute inset-0 bg-slate-900/40 pointer-events-auto"
            style={{
              willChange: 'clip-path',
              transition: isStepChanging ? 'clip-path 0.3s ease-in-out' : 'none',
              clipPath: `polygon(
              0% 0%, 
              0% 100%, 
              ${rect.left}px 100%, 
              ${rect.left}px ${rect.top}px, 
              ${rect.right}px ${rect.top}px, 
              ${rect.right}px ${rect.bottom}px, 
              ${rect.left}px ${rect.bottom}px, 
              ${rect.left}px 100%, 
              100% 100%, 
              100% 0%
            )`
          }}
          onClick={closeTour}
        />
      ) : (
        <div className="absolute inset-0 bg-slate-900/60 pointer-events-auto" onClick={closeTour} />
      )}
      
      {rect && (
          <motion.div
            initial={false}
            animate={{
              top: rect.top - 4,
              left: rect.left - 4,
              width: rect.width + 8,
              height: rect.height + 8,
            }}
            transition={{ 
              duration: isStepChanging ? 0.3 : 0, 
              ease: "easeInOut" 
            }}
            style={{ willChange: 'top, left, width, height' }}
            className="absolute border-4 border-sky-400 rounded-2xl z-[1001] pointer-events-none"
          />
        )}

        <div 
          className={`absolute inset-0 flex items-center justify-center p-4 ${cardPosition ? 'pointer-events-none' : ''}`}
        >
          <AnimatePresence mode="wait">
            <motion.div 
              key={currentStep}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              style={cardPosition ? { 
                position: 'fixed', 
                top: cardPosition.top, 
                left: cardPosition.left,
                margin: 0,
                width: window.innerWidth < 640 ? 'calc(100vw - 32px)' : '400px',
                maxWidth: '100vw'
              } : {
                position: 'relative',
                width: window.innerWidth < 640 ? 'calc(100vw - 32px)' : '400px',
                maxWidth: '100vw'
              }}
              className="bg-white dark:bg-slate-800 rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-6 md:p-8 border border-slate-200 dark:border-slate-700 pointer-events-auto z-[1002]"
            >
            <div className="flex flex-col space-y-3 sm:space-y-4">
              <div className="flex justify-between items-start">
                <h3 className="text-lg sm:text-xl font-black text-slate-900 dark:text-white leading-tight pr-6">
                  {step.title} ({currentStep + 1}/{steps.length})
                </h3>
                <button 
                  onClick={closeTour}
                  className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors absolute right-4 top-4 sm:static"
                >
                  <X size={18} />
                </button>
              </div>
              
              <p className="text-sm sm:text-base text-slate-600 dark:text-slate-300 font-medium leading-relaxed">
                {step.description}
              </p>

              <div className="flex items-center justify-between pt-2 sm:pt-4 gap-4">
                <div className="flex gap-1 overflow-hidden">
                  {steps.map((_, i) => (
                    <div 
                      key={i} 
                      className={`h-1 sm:h-1.5 rounded-full transition-all duration-300 ${
                        i === currentStep 
                          ? 'w-4 sm:w-6 bg-sky-500' 
                          : 'w-1 sm:w-1.5 bg-slate-200 dark:bg-slate-700'
                      }`}
                    />
                  ))}
                </div>
                
                <div className="flex gap-1.5 sm:gap-2">
                  <button
                    onClick={handlePrev}
                    className="p-1.5 sm:p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                  >
                    <ChevronLeft size={window.innerWidth < 640 ? 20 : 24} />
                  </button>
                  <button
                    onClick={handleNext}
                    className={`px-4 sm:px-6 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-sm sm:text-base font-bold transition-all flex items-center gap-1.5 sm:gap-2 ${
                      isLast 
                        ? 'bg-sky-500 hover:bg-sky-600 text-white' 
                        : 'bg-sky-50 hover:bg-sky-100 text-sky-600 dark:bg-sky-900/30 dark:hover:bg-sky-900/50 dark:text-sky-400'
                    }`}
                  >
                    {isLast ? t.invoice.onboarding.steps.final.button : t.common.next}
                    {!isLast && <ChevronRight size={window.innerWidth < 640 ? 16 : 18} />}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
  </div>
);
}
